import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { LogOut, UserRound } from 'lucide-react';
import { ActivityBell } from '../../components/activity/ActivityBell';
import { ActivityListModal } from '../../components/activity/ActivityListModal';
import { Avatar } from '../../components/data-display/Avatar';
import { logout } from '../../services/authService';
import { tierLabel } from '../../lib/auth/useSession';
import type { Profile } from '../../types/profile';

/**
 * Port of the DS `AdminTopBar` + the kit's right-hand cluster.
 *
 * Sticky 64px, cream .82 + blur(14px), brown hairline. Left: breadcrumb over
 * page title. Centre: the screen's search (slot — a screen fills it once its
 * search is wired to an endpoint). Right: the activity bell (unread count over
 * WebSocket; every notification, event and alert to this profile is an
 * activity), divider, then name + tier over the avatar (Account / Sign out).
 */
export function Topbar({
  breadcrumb,
  title,
  search,
  profile,
}: {
  breadcrumb?: string;
  title?: string;
  search?: ReactNode;
  profile: Profile;
}) {
  const [activityOpen, setActivityOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 flex h-topbar shrink-0 items-center gap-4 border-b border-[rgba(143,68,32,.1)] bg-[rgba(241,237,225,.82)] px-6 font-body backdrop-blur-[14px]">
      <div className="min-w-0">
        {breadcrumb && (
          <div className="text-[11px] font-semibold uppercase tracking-[.14em] text-muted">{breadcrumb}</div>
        )}
        {title && (
          <div className="truncate font-heading text-[19px] font-bold tracking-[-.01em] text-brown-deep">{title}</div>
        )}
      </div>
      <div className="flex min-w-0 flex-1 justify-center">{search}</div>
      <div className="flex shrink-0 items-center gap-2">
        <ActivityBell onClick={() => setActivityOpen(true)} />
        <div className="mx-1 h-[26px] w-px bg-[rgba(143,68,32,.14)]" aria-hidden="true" />
        <UserMenu profile={profile} />
      </div>
      <ActivityListModal isOpen={activityOpen} onClose={() => setActivityOpen(false)} />
    </header>
  );
}

function UserMenu({ profile }: { profile: Profile }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = profile.displayName || [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-[9px] rounded-pill border-0 bg-transparent px-1.5 py-1 font-body"
      >
        <div className="text-right leading-[1.25]">
          <div className="text-[13px] font-semibold text-brown-deep">{name}</div>
          <div className="text-[11px] text-muted">{tierLabel(profile.roles)}</div>
        </div>
        <Avatar src={profile.avatarUrl} name={name} size="md" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-[60] min-w-[180px] overflow-hidden rounded-app-card border border-panel-border bg-white py-1 shadow-lg"
        >
          <Link
            to="/account"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink no-underline hover:bg-[rgba(143,68,32,.04)] hover:text-ink"
          >
            <UserRound size={16} aria-hidden="true" /> Account
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => void logout()}
            className="flex w-full items-center gap-2.5 border-0 bg-transparent px-4 py-2.5 text-left text-sm text-ink hover:bg-[rgba(143,68,32,.04)]"
          >
            <LogOut size={16} aria-hidden="true" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
